import type { CrudAdapter } from '@botto/core';
import { useCrudDataSource } from '@botto/core';
import { smsClient } from '../client';
import type { RoutingChoice, RoutingChoiceCreate, RoutingChoiceUpdate } from '../types';

export function createRoutingChoicesAdapter(
  groupId: number,
): CrudAdapter<RoutingChoice, RoutingChoiceCreate, RoutingChoiceUpdate> {
  return {
    async list({ offset, limit, sortName, sortDirection, filter }) {
      const { data, error } = await smsClient.GET('/groups/{group_id}/routing_choices/', {
        params: {
          path: { group_id: groupId },
          query: {
            offset,
            limit,
            'sort.name': sortName ?? 'id',
            'sort.direction': (sortDirection ?? 'asc') as 'asc' | 'desc',
            ...(filter?.aggregator_id != null
              ? { aggregator_id: Number(filter.aggregator_id) }
              : {}),
            ...(filter?.state != null ? { state: String(filter.state) as 'ACTIVE' | 'INACTIVE' } : {}),
            ...(filter?.parent_id != null ? { parent_id: Number(filter.parent_id) } : {}),
          },
        },
      });
      if (error) throw error;
      return { items: data?.items ?? [], total: data?.total ?? 0 };
    },
    async create(payload) {
      const body: RoutingChoiceCreate = {
        ...payload,
        group_id: groupId,
      };
      const { data, error } = await smsClient.POST('/groups/{group_id}/routing_choices/', {
        params: { path: { group_id: groupId } },
        body,
      });
      if (error) throw error;
      return { id: data?.id ?? 0 };
    },
    async update(payload) {
      const { data, error } = await smsClient.PUT('/groups/{group_id}/routing_choices/', {
        params: { path: { group_id: groupId } },
        body: { ...payload, group_id: groupId },
      });
      if (error) throw error;
      return (data ?? payload) as RoutingChoice;
    },
    async byKey(id) {
      const { data, error } = await smsClient.GET('/groups/{group_id}/routing_choices/{item_id}', {
        params: { path: { group_id: groupId, item_id: Number(id) } },
      });
      if (error) throw error;
      return data as RoutingChoice;
    },
    async remove(id) {
      const { error } = await smsClient.DELETE('/groups/{group_id}/routing_choices/{item_id}', {
        params: { path: { group_id: groupId, item_id: Number(id) } },
      });
      if (error) throw error;
    },
  };
}

export function createRoutingChoicesStore(groupId: number) {
  return useCrudDataSource(createRoutingChoicesAdapter(groupId), { defaultPageSize: 1000 });
}

/** Child / failover routing choices under a parent choice. */
export function createChildRoutingChoicesStore(groupId: number, parentId: number) {
  const base = createRoutingChoicesAdapter(groupId);
  const adapter: CrudAdapter<RoutingChoice, RoutingChoiceCreate, RoutingChoiceUpdate> = {
    async list(params) {
      const result = await base.list({ ...params, offset: 0, limit: 1000 });
      const filtered = result.items.filter((item) => item.parent_id === parentId);
      const offset = params.offset ?? 0;
      const limit = params.limit ?? 1000;
      return {
        items: filtered.slice(offset, offset + limit),
        total: filtered.length,
      };
    },
    async create(payload) {
      if (!base.create) throw new Error('create not supported');
      return base.create({ ...payload, parent_id: parentId, group_id: groupId });
    },
    async update(payload) {
      if (!base.update) throw new Error('update not supported');
      return base.update(payload);
    },
    async byKey(id) {
      if (!base.byKey) throw new Error('byKey not supported');
      return base.byKey(id);
    },
    async remove(id) {
      if (!base.remove) throw new Error('remove not supported');
      await base.remove(id);
    },
  };
  return useCrudDataSource(adapter, { defaultPageSize: 100 });
}
